import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useBoardId } from "@contexts/BoardContext";
import { useApi } from "@api/useApi";
import {
  getBillingRefusal,
  getBillingRefusalFromPopupError,
  getConnectionBillingState,
  isConnectionsSignalREventType,
  useBillingSummary,
  type BillingRefusal,
  type BoardConnection,
  type ConnectionsAuthorisationMode,
  type ConnectionsAuthorisationRequest,
  type ConnectionEditState,
  type ConnectionBillingState,
  type ConnectionEditStateUpdater,
  type Source,
  type Status,
  useBoardSignalRSubscription,
  useConnectionsViewModel as useSharedConnectionsViewModel,
} from "@onetasklist/shared";
import { toValidHexColour } from "@onetasklist/shared/utils/colorUtils";
import { useConfirmDialog } from "@components/useConfirmDialog";
import { useSelectOrgDialog } from "./select-org/useSelectOrgDialog";
import { useCataloguePlannedSources } from "./catalogue/CatalogueView.i18n";
import { useResolvedSources } from "./catalogue/CatalogueSourceResolver";
import { useAuthPopup } from "@features/auth-popup/useAuthPopup";
import { createDebugLogger } from "@utils/debugLog";
import { sourceAuthSetupUsesDialog } from "./source-auth-setup";

const log = createDebugLogger("auth-popup:connections");

export type { ConnectionEditState, ConnectionEditStateUpdater } from "@onetasklist/shared";

export type PrivacyDialogState =
  | { mode: "all" }
  | { mode: "source"; sourceId: string }
  | null;

export interface AuthorisationInfoDialogState {
  title: string;
  iframeUrl: string;
}

const toAbsoluteUrl = (url: string, regionBaseUrl?: string): string => {
  const normalizedRegionBase = regionBaseUrl?.trim();

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  if (normalizedRegionBase && /^\//.test(url)) {
    return `${normalizedRegionBase.replace(/\/$/, "")}${url}`;
  }

  return new URL(url, window.location.origin).toString();
};

// Old re-auth URLs encode the auth protocol via path segment:
//   OAuth1:  contains /oauth1/
//   OAuth2:  everything else
const detectAuthProtocol = (url: string): "oauth2" | "oauth1" => {
  if (/\/oauth1\//i.test(url)) return "oauth1";
  return "oauth2";
};

// Old re-auth URLs: /api/boards/{boardId}/authorizeconnection/{connectionId}
//                   /api/boards/{boardId}/oauth1/authorizeconnection/{connectionId}
const extractConnectionId = (url: string): string | null => {
  const match = /\/authorizeconnection\/([a-f0-9-]+)/i.exec(url);
  return match?.[1] ?? null;
};

const applyAuthPageToken = (
  pageTemplate: string,
  token: string,
  value: string,
): string => pageTemplate.replace(token, encodeURIComponent(value));

const buildAuthorisationInfoPageUrl = ({
  pageTemplate,
  boardId,
  source,
  state,
  error,
  errorDescription,
}: {
  pageTemplate: string;
  boardId?: string;
  source: Source;
  state?: string;
  error?: string;
  errorDescription?: string;
}): string => {
  let next = pageTemplate;
  next = applyAuthPageToken(next, "{boardId}", boardId || "");
  next = applyAuthPageToken(next, "{sourceId}", source.id);
  next = applyAuthPageToken(next, "{connectionId}", "");
  next = applyAuthPageToken(next, "{state}", state || "");
  next = applyAuthPageToken(next, "{error}", error || "");
  next = applyAuthPageToken(next, "{errorDescription}", errorDescription || "");
  return toAbsoluteUrl(next);
};

export interface ConnectionsPageViewModel {
  isLoading: boolean;
  isCreatingConnection: boolean;
  isSavingConnection: boolean;
  isDeletingConnection: boolean;
  isAuthorisingConnection: boolean;
  hasError: boolean;
  errorMessage: string;
  actionErrorMessage: string;
  /** A connect or un-archive the plan refused, or billing couldn't check: shown as an upgrade prompt. */
  billingRefusal: BillingRefusal | null;
  clearBillingRefusal: () => void;
  statuses: Status[];
  allSources: Source[];
  connections: BoardConnection[];
  connectionSearchText: string;
  setConnectionSearchText: (value: string) => void;
  filteredConnections: BoardConnection[];
  /** Connection id -> the connection's colour, when it has a usable one. */
  connectionAccentColours: Record<string, string>;
  filteredSources: Source[];
  connectedPrivacySources: Source[];
  privacyDialog: PrivacyDialogState;
  privacySources: Source[];
  authorisationInfoDialog: AuthorisationInfoDialogState | null;
  searchText: string;
  editingConnection: ConnectionEditState | null;
  canReAuthoriseEditingConnection: boolean;
  isDirty: boolean;
  hasVotedForSource: (sourceId: string) => boolean;
  setSearchText: (value: string) => void;
  voteForSource: (sourceId: string) => void;
  connectSource: (sourceId: string) => Promise<void>;
  /** Called by source auth-setup components to provide pre-OAuth field values (e.g. subdomain, hostUrl). */
  setSourceSetup: (setup: Record<string, string>) => void;
  openAllPrivacyDialog: () => void;
  openSourcePrivacyDialog: (sourceId: string) => void;
  closePrivacyDialog: () => void;
  closeAuthorisationInfoDialog: () => void;
  openConnectionSettings: (connection: BoardConnection) => void;
  closeConnectionSettings: () => void;
  updateEditingConnection: ConnectionEditStateUpdater;
  saveEditingConnection: () => Promise<void>;
  /** Saves a connection as given, without it having to be open for editing. */
  saveConnection: (connection: BoardConnection) => Promise<boolean>;
  deleteEditingConnection: () => Promise<void>;
  reAuthoriseEditingConnection: () => Promise<void>;
  /** Whether this connection's source offers a re-authorisation flow at all. */
  canReAuthoriseConnection: (connection: BoardConnection) => boolean;
  /** The trial or paused state the card shows for a connection under the user's plan, if any. */
  getBillingState: (connection: BoardConnection) => ConnectionBillingState | null;
  /** Re-runs authorisation for any connection, straight from its card in the list. */
  reAuthoriseConnection: (connection: BoardConnection) => Promise<boolean>;
  /**
   * The list's Reconnect: sources with a pre-OAuth setup dialog (VSTS, Zendesk, Monday) open
   * it first, as the settings page does; the rest re-authorise straight away.
   */
  requestReconnect: (connection: BoardConnection) => void;
  /** Source id of the setup dialog the list's Reconnect opened, or null when none is open. */
  reconnectSetupSourceId: string | null;
  confirmReconnectSetup: () => Promise<void>;
  closeReconnectSetup: () => void;
  reload: () => Promise<void>;
  confirmDialog: ReactNode;
  selectOrgDialog: ReactNode;
}

export const useConnectionsViewModel = (): ConnectionsPageViewModel => {
  const boardId = useBoardId();
  const api = useApi();
  // The summary other screens already fetched. No retries: without it the cards simply show no plan state.
  const { data: billing } = useBillingSummary(api.billing, { retry: false });
  const getBillingState = useCallback(
    (connection: BoardConnection) => getConnectionBillingState(connection, billing, new Date()),
    [billing],
  );
  const { t } = useTranslation();
  const { confirm, confirmDialog } = useConfirmDialog();
  const { selectOrg, selectOrgDialog } = useSelectOrgDialog();
  const { openAuthPopup } = useAuthPopup();
  const [privacyDialog, setPrivacyDialog] = useState<PrivacyDialogState>(null);
  const [connectionSearchText, setConnectionSearchText] = useState("");
  const [authorisationInfoDialog, setAuthorisationInfoDialog] =
    useState<AuthorisationInfoDialogState | null>(null);
  // Refusals on the OAuth and PAT paths, which don't go through the shared create call.
  const [authBillingRefusal, setAuthBillingRefusal] = useState<BillingRefusal | null>(null);
  const sharedRef = useRef<{ reload: () => Promise<void> } | null>(null);
  const sourceSetupRef = useRef<Record<string, string>>({});
  const [reconnectSetupConnection, setReconnectSetupConnection] =
    useState<BoardConnection | null>(null);

  const signalR = useBoardSignalRSubscription({
    boardId,
    onNotification: ({ eventType }) => {
      if (!isConnectionsSignalREventType(eventType)) {
        return;
      }

      void sharedRef.current?.reload();
    },
  });

  const voteStorage = useMemo(
    () => ({
      getItem: (key: string) => window.localStorage.getItem(key),
      setItem: (key: string, value: string) =>
        window.localStorage.setItem(key, value),
    }),
    [],
  );

  const confirmDeleteConnection = useCallback(
    async (): Promise<boolean> =>
      confirm({
        title: t("board.connections.dialog.delete"),
        content: t("board.connections.dialog.deleteConfirm"),
        confirmLabel: t("board.connections.dialog.delete"),
        cancelLabel: t("common.actions.cancel"),
        intent: "destructive",
      }),
    [confirm, t],
  );

  const getAuthUrl = useCallback(
    async (
      url: string,
      mode: ConnectionsAuthorisationMode,
      source: Source,
    ): Promise<string | null> => {
      const protocol = detectAuthProtocol(url);
      const setup = sourceSetupRef.current;

      try {
        if (!boardId) return null;

        if (mode === "connect") {
          log("getAuthUrl: requesting oauthStart", { sourceId: source.id, protocol });
          const { authUrl } = await api.boards.oauthStart(
            boardId,
            protocol,
            source.id,
            setup,
          );
          return authUrl;
        }

        const connectionId = extractConnectionId(url);
        if (!connectionId) {
          log(`getAuthUrl: could not extract connectionId from re-auth URL: ${url}`);
          console.warn(
            `[Connections] Could not extract connectionId from re-auth URL: ${url}`,
          );
          return null;
        }

        log("getAuthUrl: requesting oauthReauthorize", { sourceId: source.id, protocol, connectionId });
        const { authUrl } = await api.boards.oauthReauthorize(
          boardId,
          protocol,
          connectionId,
          setup,
        );
        return authUrl;
      } catch (err) {
        // The plan is checked before the provider redirect, so the user never authorises for nothing.
        const refusal = getBillingRefusal(err);
        if (refusal) {
          log("getAuthUrl: refused by billing", { sourceId: source.id, kind: refusal.kind });
          setAuthBillingRefusal(refusal);
          return null;
        }

        console.error(
          `[Connections] Failed to fetch auth URL for ${source.id}`,
          err,
        );
        return null;
      }
    },
    [api.boards, boardId],
  );

  const launchAuthorisation = useCallback(
    async ({
      mode,
      url,
      source,
    }: ConnectionsAuthorisationRequest): Promise<boolean> => {
      setAuthorisationInfoDialog(null);
      setAuthBillingRefusal(null);
      log("launchAuthorisation: starting", { mode, sourceId: source.id });

      // VSTS PAT: create connection directly — no OAuth popup needed.
      const setup = sourceSetupRef.current;
      if (source.id.toLowerCase() === "vsts" && setup.authMethod === "pat") {
        if (!boardId || !setup.pat) return false;
        try {
          await api.boards.vstsPatConnect(boardId, setup.pat);
          return true;
        } catch (err) {
          const refusal = getBillingRefusal(err);
          if (refusal) {
            setAuthBillingRefusal(refusal);
            return false;
          }

          console.error("[Connections] VSTS PAT connect failed", err);
          return false;
        }
      }

      const authUrl = await getAuthUrl(url, mode, source);
      if (!authUrl) {
        log("launchAuthorisation: no authUrl resolved, aborting", { sourceId: source.id });
        return false;
      }

      const result = await openAuthPopup(authUrl, source.displayName);
      log("launchAuthorisation: popup result", {
        mode,
        sourceId: source.id,
        result,
      });

      if (result.cancelled) {
        log("launchAuthorisation: popup cancelled/closed without completing");
        return false;
      }

      if (result.error) {
        // Billing refusals are the same for every source, so they come before the per-source error pages.
        const popupRefusal = getBillingRefusalFromPopupError(result.error);
        if (popupRefusal) {
          log("launchAuthorisation: refused by billing", { sourceId: source.id, error: result.error });
          setAuthBillingRefusal(popupRefusal);
          return false;
        }

        const errorPageTemplate = source.clientErrorPages?.[result.error];

        log("launchAuthorisation: popup returned an error", {
          sourceId: source.id,
          error: result.error,
          hasErrorPage: !!errorPageTemplate,
        });

        if (errorPageTemplate) {
          setAuthorisationInfoDialog({
            title: t("board.connections.authorise.unableToAuthoriseTitle", {
              source: source.displayName,
            }),
            iframeUrl: buildAuthorisationInfoPageUrl({
              pageTemplate: errorPageTemplate,
              boardId,
              source,
              state: result.state,
              error: result.error,
              errorDescription: result.errorDescription,
            }),
          });
        }

        return false;
      }

      // Sources like Dynamics authorise twice: the first popup only yields a
      // discovery token, so the environment is picked here — in the main window,
      // where the API call carries the user's JWT — before the second leg.
      if (result.requiresOrgSelection && result.connectionId && boardId) {
        log("launchAuthorisation: source needs an environment picked", {
          sourceId: source.id,
          connectionId: result.connectionId,
        });

        const orgAuthUrl = await selectOrg({
          boardId,
          connectionId: result.connectionId,
          sourceTypeId: source.id,
          sourceDisplayName: source.displayName,
        });

        if (!orgAuthUrl) {
          log("launchAuthorisation: environment selection cancelled or failed");
          return false;
        }

        const orgResult = await openAuthPopup(orgAuthUrl, source.displayName);
        log("launchAuthorisation: environment popup result", {
          sourceId: source.id,
          result: orgResult,
        });

        if (orgResult.cancelled || orgResult.error) {
          return false;
        }
      }

      // Both modes end the same way now. Re-authorisation used to ask the user to
      // trigger a reload, because the popup could not report back reliably and the
      // opener had no way to know the flow had finished. It can now, so the caller
      // refreshes on its own.
      log("launchAuthorisation: succeeded", { mode, sourceId: source.id });
      return true;
    },
    [api.boards, boardId, getAuthUrl, openAuthPopup, selectOrg, t],
  );

  const plannedSources = useCataloguePlannedSources();

  const shared = useSharedConnectionsViewModel({
    boardId,
    hubConnectionId: signalR.connectionId,
    boardsApi: api.boards,
    sourcesApi: api.sources,
    loadErrorMessage: t("board.connections.errors.loadConnections"),
    saveErrorMessage: t("board.connections.errors.saveConnection"),
    deleteErrorMessage: t("board.connections.errors.deleteConnection"),
    invalidConfigurationMessage: t(
      "board.connections.errors.invalidConfiguration",
    ),
    confirmDeleteConnection,
    launchAuthorisation,
    voteStorage,
    feedbackApi: api.feedback,
    additionalPlannedSources: plannedSources,
  });

  useLayoutEffect(() => {
    sharedRef.current = shared;
  });

  const resolvedAllSources = useResolvedSources(shared.allSources);
  const resolvedFilteredSources = useResolvedSources(shared.filteredSources);

  const privacySources = useMemo(() => {
    if (!privacyDialog) {
      return [] as Source[];
    }

    if (privacyDialog.mode === "all") {
      return shared.connectedPrivacySources;
    }

    return resolvedFilteredSources.filter(
      (source) => source.id === privacyDialog.sourceId,
    );
  }, [privacyDialog, shared.connectedPrivacySources, resolvedFilteredSources]);

  const filteredConnections = useMemo(() => {
    const query = connectionSearchText.trim().toLowerCase();
    if (!query) {
      return shared.connections;
    }

    return shared.connections.filter((connection) =>
      (connection.name ?? connection.sourceTypeId).toLowerCase().includes(query),
    );
  }, [shared.connections, connectionSearchText]);

  // The colour the board gives a connection, which its card wears as a stripe
  // down the leading edge — the same cue the task cards use, so a Jira card
  // reads as Jira wherever it turns up. A colour the board never set, or one
  // that is not a hex value, leaves the card plain rather than striped.
  const connectionAccentColours = useMemo(() => {
    const colours: Record<string, string> = {};

    for (const connection of shared.connections) {
      const colour = toValidHexColour(connection.colour);
      if (colour) {
        colours[connection.id] = colour;
      }
    }

    return colours;
  }, [shared.connections]);

  return {
    isLoading: shared.isLoading,
    isCreatingConnection: shared.isCreatingConnection,
    isSavingConnection: shared.isSavingConnection,
    isDeletingConnection: shared.isDeletingConnection,
    isAuthorisingConnection: shared.isAuthorisingConnection,
    hasError: shared.hasError,
    errorMessage: shared.errorMessage,
    actionErrorMessage: shared.actionErrorMessage,
    billingRefusal: authBillingRefusal ?? shared.billingRefusal,
    clearBillingRefusal: () => {
      setAuthBillingRefusal(null);
      shared.clearBillingRefusal();
    },
    statuses: shared.statuses,
    allSources: resolvedAllSources,
    connections: shared.connections,
    connectionSearchText,
    setConnectionSearchText,
    filteredConnections,
    connectionAccentColours,
    filteredSources: resolvedFilteredSources,
    connectedPrivacySources: shared.connectedPrivacySources,
    privacyDialog,
    privacySources,
    authorisationInfoDialog,
    searchText: shared.searchText,
    editingConnection: shared.editingConnection,
    canReAuthoriseEditingConnection: shared.canReAuthoriseEditingConnection,
    isDirty: shared.isDirty,
    hasVotedForSource: shared.hasVotedForSource,
    setSearchText: shared.setSearchText,
    voteForSource: shared.voteForSource,
    connectSource: shared.connectSource,
    setSourceSetup: (setup: Record<string, string>) => {
      sourceSetupRef.current = setup;
    },
    openAllPrivacyDialog: () => setPrivacyDialog({ mode: "all" }),
    openSourcePrivacyDialog: (sourceId: string) =>
      setPrivacyDialog({ mode: "source", sourceId }),
    closePrivacyDialog: () => setPrivacyDialog(null),
    closeAuthorisationInfoDialog: () => setAuthorisationInfoDialog(null),
    openConnectionSettings: shared.openConnectionSettings,
    closeConnectionSettings: shared.closeConnectionSettings,
    updateEditingConnection: shared.updateEditingConnection,
    saveEditingConnection: shared.saveEditingConnection,
    saveConnection: shared.saveConnection,
    deleteEditingConnection: shared.deleteEditingConnection,
    reAuthoriseEditingConnection: shared.reAuthoriseEditingConnection,
    canReAuthoriseConnection: shared.canReAuthoriseConnection,
    getBillingState,
    reAuthoriseConnection: shared.reAuthoriseConnection,
    requestReconnect: (connection: BoardConnection) => {
      if (!sourceAuthSetupUsesDialog(connection.sourceTypeId)) {
        void shared.reAuthoriseConnection(connection);
        return;
      }

      // The dialog reports its values as they change; don't carry over another source's.
      sourceSetupRef.current = {};
      setReconnectSetupConnection(connection);
    },
    reconnectSetupSourceId: reconnectSetupConnection?.sourceTypeId ?? null,
    confirmReconnectSetup: async () => {
      const connection = reconnectSetupConnection;
      setReconnectSetupConnection(null);
      if (connection) {
        await shared.reAuthoriseConnection(connection);
      }
    },
    closeReconnectSetup: () => setReconnectSetupConnection(null),
    reload: shared.reload,
    confirmDialog,
    selectOrgDialog,
  };
};
